import {CSSProperties, FC, SyntheticEvent, useCallback, useEffect, useRef, useState} from 'react';
import './OkCancel.less';
import './Buttons.less';
import ButtonGroup from "./ButtonGroup";
import Button from "./Button";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck, faTimes} from '@fortawesome/pro-light-svg-icons';
import {faTrashAlt} from '@fortawesome/pro-solid-svg-icons';
import i18n from '../../i18n/i18n';

export type OkCancelProps = {
	label: string;
	direction?: 'row' | 'column',
	disabled: boolean,
	disabledMessage?: string,
	cancelDisabled?: boolean,
	onSubmit?: (e: SyntheticEvent) => void | Promise<void>,
	onClick?: (e: SyntheticEvent) => void | Promise<void>,
	onCancel?: (e: SyntheticEvent) => void,
	onTop?: boolean,
	position?: 'left' | 'center' | 'right' | 'top' | 'bottom',
	style?: 'circle' | 'square',
	onDelete?: (e: SyntheticEvent) => void,
	deleteDisabled?: boolean,
	useDisabledMessage?: boolean,
	/**
	 * Where the disabledMessage sits relative to {label, buttons}.
	 * - 'left'   — always on the left (wide layouts)
	 * - 'bottom' — always below
	 * - 'auto'   — responsive: left when the container is wide enough, below otherwise
	 *
	 * Responsive mode is driven by a CSS container query on the outer wrapper.
	 * For sticky (onTop=true) usage, the caller's card must reserve enough top
	 * padding (~4em) so the pill never overlaps content beneath it.
	 */
	messagePosition?: 'left' | 'bottom' | 'auto',
	extraSticky?: boolean,
	justBelow?: boolean,
	/**
	 * Fine-tune the vertical position of the pill in pixels.
	 * Negative values nudge up, positive values nudge down. Applied as a
	 * CSS transform so the offset works even when it pushes the pill
	 * outside the bounds of its parent. Intended for sub-5px adjustments.
	 */
	nudgeY?: number,
	likesEntityType?: string,
	likesEntityId?: string,
	likesAreConfidential?: boolean,
}

/**
 * Simple class to add an Ok/Cancel pair.
 *
 * Optionally can style as round or square(-ish)
 */
const OkCancel: FC<OkCancelProps> = props => {
	const {direction = 'row', position = 'right', messagePosition = 'auto'} = props;

	// Re-entry guard: prevent rapid re-clicks from stacking duplicate save flows.
	// Ref handles the synchronous check (immune to closure-capture races on same-render
	// double-tap); state drives the visual disable so the button visibly grays out.
	const isSavingRef = useRef(false);
	const isMountedRef = useRef(true);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => () => { isMountedRef.current = false; }, []);

	const onClick = useCallback((e: SyntheticEvent) => {
		if (!props.onClick || isSavingRef.current) return;
		isSavingRef.current = true;
		setIsSaving(true);
		// 100ms setTimeout is intentional — gives focused inputs' onBlur a chance to flush
		// their typed value into form state before the save handler reads it.
		setTimeout(async () => {
			try { await props.onClick!(e); }
			catch (err) { console.error(err); }
			finally {
				isSavingRef.current = false;
				if (isMountedRef.current) setIsSaving(false);
			}
		}, 100);
	}, [props]);

	const onSubmit = useCallback((e: SyntheticEvent) => {
		if (!props.onSubmit || isSavingRef.current) return;
		isSavingRef.current = true;
		setIsSaving(true);
		setTimeout(async () => {
			try { await props.onSubmit!(e); }
			catch (err) { console.error(err); }
			finally {
				isSavingRef.current = false;
				if (isMountedRef.current) setIsSaving(false);
			}
		}, 100);
	}, [props]);

	const onCancel = useCallback((e: SyntheticEvent) => {
		if (props.onCancel) setTimeout(() => props.onCancel(e), 100);
	}, [props]);

	const onDelete = useCallback((e: SyntheticEvent) => {
		if (props.onDelete) setTimeout(() => props.onDelete(e), 100);
	}, [props]);

	const deleteButton = props.onDelete ? <>
		<Button id={`DELETE_${props.label}`}
				label={<FontAwesomeIcon icon={faTrashAlt}/>}
				severity='danger'
				type='circle'
				size='medium'
				style={{backgroundColor: '#800020', border: 'none'}}
				disabled={props.deleteDisabled}
				onClick={onDelete}
				title='Delete'
		/>
		<div className='ok_cancel__separator'/>
	</> : null;

	// `location` selects between top/bottom container styling. Top is the default;
	// callers that genuinely want the pill at the bottom (e.g. modal dialogs whose
	// own footer is the natural anchor) pass `onTop={false}` to opt out.
	const location = props.onTop === false ? 'bottom' : 'top';
	const showMessage = !!(props.useDisabledMessage && props.disabledMessage);

	const containerClasses = [
		'ok_cancel__container',
		`ok_cancel__container--${location}`,
		`ok_cancel__container--msg-${messagePosition}`,
		props.justBelow ? 'ok_cancel__container--just-below' : '',
	].filter(Boolean).join(' ');

	const containerStyle: CSSProperties = {zIndex: 99};
	if (props.nudgeY) containerStyle.transform = `translateY(${props.nudgeY}px)`;

	const result = (
		<div className={containerClasses} style={containerStyle}>
			<div className='ok_cancel'>
				{deleteButton}
				<div className='ok_cancel__label'>{props.label}</div>
				<ButtonGroup direction={direction} position={position}>

					{/* On Cancel */}
					{props.onCancel && <Button id={`CANCEL_${props.label}`}
											   label={<FontAwesomeIcon icon={faTimes}/>}
											   severity='danger'
											   type='circle'
											   size='medium'
											   disabled={props.cancelDisabled}
											   onClick={onCancel}
											   title={i18n.cs('cancel')}
					/>}

					{/* On Ok/Submit */}
					{(props.onClick || props.onSubmit) && <Button id={`OK_${props.label}`}
																  label={<FontAwesomeIcon icon={faCheck}/>}
																  severity='success'
																  type='circle'
																  size='medium'
																  disabled={props.disabled || isSaving}
																  onSubmit={props.onSubmit && onSubmit}
																  onClick={props.onClick && onClick}
																  title={i18n.cs('submit')}
																  useDelay={true}
					/>}
				</ButtonGroup>
				{showMessage && <div className='ok_cancel__disabled-message'>{props.disabledMessage}</div>}
			</div>
		</div>
	);

	if (props.extraSticky) return <div
		className={`ok_cancel_wrapper ok_cancel_wrapper--${location}`}>{result}</div>;
	else return result;
}

export default OkCancel;
